<template>
  <div
    class="settings-menu-item"
    :class="{
      'settings-menu-item--selected': selected,
      'settings-menu-item--disabled': disabled,
    }"
    :title="disabled ? tooltip : undefined"
    @click="handleClick"
  >
    <component :is="icon" v-if="icon" />
    <span class="label">{{ label }}</span>
    <span v-if="badge" class="badge" :class="badgeClass">{{ badge }}</span>
    <LockIcon v-if="!isEntitledTo" class="lock-icon" />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, type Component } from "vue";

import LockIcon from "@/components/icons/LockIcon.vue";
import * as inject from "@/components/inject";
import { openUpgradeModal } from "@/components/modals/upgrade";

export default defineComponent({
  name: "SettingsMenuItem",
  components: {
    LockIcon,
  },
  props: {
    icon: {
      type: [Object, Function] as PropType<Component>,
      required: false,
    },
    label: {
      type: String,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    badge: {
      type: String,
      required: false,
    },
    badgeClass: {
      type: String,
      default: "beta-tag",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      required: false,
    },
    entitlementKey: {
      type: String,
      required: false,
    },
    organizationId: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  emits: {
    click: () => true,
  },
  setup(props, { emit }) {
    const billing = inject.billing();

    // billing is async-initialized; keep entitlement reactive as it becomes available
    const entitlementRef = computed(() => {
      if (!props.entitlementKey) return undefined;
      return billing.value?.isEntitledTo(
        props.entitlementKey,
        props.organizationId
      );
    });

    // Check entitlement if entitlementKey is provided
    const isEntitledTo = computed(() => {
      if (!props.entitlementKey) return true;
      return entitlementRef.value?.value ?? false;
    });

    const handleClick = () => {
      if (props.disabled) {
        return;
      }

      if (props.entitlementKey && !isEntitledTo.value) {
        openUpgradeModal(billing, props.organizationId, props.entitlementKey);
        return;
      }
      emit("click");
    };

    return {
      isEntitledTo,
      handleClick,
    };
  },
});
</script>

<style scoped lang="scss">
.settings-menu-item {
  display: flex;
  column-gap: 10px;
  @extend %font-body-regular;
  padding: 6px 12px 7px 12px;
  border-radius: 7px;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  @include transition(all 0.1s ease-out);
  @include themed() {
    color: t($grey-60);
  }

  &:hover {
    @include themed() {
      color: t($grey-70);
      background: t($bg-menu-hover);
    }
  }

  &:active {
    @include themed() {
      background: t($grey-15);
    }
  }

  :deep(svg .fill) {
    @include themed() {
      fill: t($grey-60);
    }
  }

  &.settings-menu-item--selected {
    font-weight: 500;

    @include themed() {
      color: t($primary);
      background: t($grey-10);
    }

    :deep(.fill) {
      @include themed() {
        fill: t($primary);
      }
    }

    :deep(.stroke) {
      @include themed() {
        stroke: t($primary);
      }
    }
  }

  &.settings-menu-item--disabled {
    cursor: not-allowed;
    opacity: 0.5;

    @include themed() {
      color: t($grey-30);
    }

    :deep(svg .fill) {
      @include themed() {
        fill: t($grey-30);
      }
    }

    &:hover {
      @include themed() {
        color: t($grey-30);
        background: transparent;
      }

      :deep(svg .fill) {
        @include themed() {
          fill: t($grey-30);
        }
      }
    }
  }

  svg {
    width: 18px;
    height: auto;
    margin-top: 1px;
  }

  .label {
    flex: 1;
  }

  .badge {
    &.beta-tag {
      @extend %font-small-medium;
      padding: 1px 6px;
      border-radius: $border-radius-m;

      @include themed() {
        color: t($tertiary-50);
        background: t($tertiary-10);
      }
    }
  }

  .lock-icon {
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    margin-inline-start: auto;
    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
